import { type Firestore, doc, onSnapshot } from "firebase/firestore"
import { getRolesCollectionRef, getUserRoleDocRef } from "./firestore-refs"
import { isAuthorizationDocumentId, normalizeManagedTeamIds } from "./authorization"
import type { Role } from "./types/entities"
import { resolveAssignedTenantRole } from "./assigned-tenant-role"

/** Listen to both the private assignment and its current role definition. */
export const subscribeAssignedRole = (
  db: Firestore,
  userId: string,
  onRole: (role: Role | null) => void,
  onError: (error: Error) => void,
  onManagedTeamIds?: (teamIds: string[]) => void,
  onLoading?: (loading: boolean) => void,
): (() => void) => {
  let unsubscribeRole: (() => void) | undefined
  let generation = 0
  let active = true
  onLoading?.(true)
  const unsubscribeAssignment = onSnapshot(
    getUserRoleDocRef(db, userId),
    (assignment) => {
      if (!active) return
      const current = ++generation
      unsubscribeRole?.()
      onLoading?.(true)
      onRole(null)
      onManagedTeamIds?.([])
      const roleId = assignment.data()?.role
      if (!isAuthorizationDocumentId(roleId)) {
        onLoading?.(false)
        return
      }
      unsubscribeRole = onSnapshot(
        doc(getRolesCollectionRef(db), roleId),
        (role) => {
          if (!active || generation !== current) return
          const resolved = role.exists() ? resolveAssignedTenantRole(userId, { ...role.data(), id: role.id } as Role, assignment.data()) : null
          onRole(resolved)
          onManagedTeamIds?.(resolved?.scopePolicy === "assigned-teams" ? normalizeManagedTeamIds(assignment.data()?.managedTeamIds) ?? [] : [])
          onLoading?.(false)
        },
        (error) => {
          if (active && generation === current) {
            onRole(null)
            onManagedTeamIds?.([])
            onLoading?.(false)
            onError(error)
          }
        },
      )
    },
    (error) => {
      if (!active) return
      ++generation
      unsubscribeRole?.()
      onRole(null)
      onManagedTeamIds?.([])
      onLoading?.(false)
      onError(error)
    },
  )
  return () => {
    active = false
    ++generation
    unsubscribeAssignment()
    unsubscribeRole?.()
  }
}
